import { useState, useEffect, useMemo, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Typography, Button, Space, Card, Statistic, Row, Col, Tag } from 'antd'
import { ReloadOutlined, DownloadOutlined } from '@ant-design/icons'
import { feedbackService } from '@/services/feedbackService'
import { FeedbackTable } from '@/components/feedback/FeedbackTable'
import { FeedbackFilters, FeedbackFilterState } from '@/components/feedback/FeedbackFilters'
import { useTour } from '@/hooks/useTour'
import { adminFeedbackTour } from '@/tours/admin-feedback'

const { Title, Text } = Typography

// Clean feedback ID search string (removes FB- prefix if present)
const cleanFeedbackIdSearch = (idParam: string | null | undefined): string | null => {
  if (!idParam) return null
  return idParam.toUpperCase().replace('FB-', '').trim()
}

export default function FeedbackManagementPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const idFromUrl = searchParams.get('id')

  // First Time Wizard tour
  const { TourComponent } = useTour(adminFeedbackTour)

  // Initialize filters with ID from URL if present
  const [filters, setFilters] = useState<FeedbackFilterState>(() => ({
    statuses: idFromUrl ? [] : ['New', 'In Progress'],
    dateRange: null,
    feedbackId: idFromUrl || undefined,
  }))

  // Sync URL param to filter state when URL changes (e.g., after login redirect)
  useEffect(() => {
    if (idFromUrl && filters.feedbackId !== idFromUrl) {
      setFilters(prev => ({
        ...prev,
        feedbackId: idFromUrl,
        statuses: []  // Clear status filter when filtering by ID
      }))
    }
  }, [idFromUrl])

  // Handle filter changes - sync feedbackId to URL
  const handleFilterChange = useCallback((newFilters: FeedbackFilterState) => {
    setFilters(newFilters)

    // Sync feedbackId to URL
    if (newFilters.feedbackId) {
      searchParams.set('id', newFilters.feedbackId)
      setSearchParams(searchParams, { replace: true })
    } else if (searchParams.has('id')) {
      searchParams.delete('id')
      setSearchParams(searchParams, { replace: true })
    }
  }, [searchParams, setSearchParams])

  // Get the cleaned search string for filtering (contains search)
  const feedbackIdSearch = cleanFeedbackIdSearch(filters.feedbackId)

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['feedback-admin', filters],
    queryFn: async () => {
      const response = await feedbackService.listFeedback({
        types: filters.types,
        statuses: filters.statuses,
        priorities: filters.priorities,
        categories: filters.categories,
        dateFrom: filters.dateRange?.[0],
        dateTo: filters.dateRange?.[1],
        search: filters.search,
        orgId: filters.orgId,
      })
      if (!response.success || !response.data) {
        throw new Error(response.message || 'Failed to fetch feedback')
      }
      return response.data
    },
    staleTime: 1000 * 60, // 1 minute
  })

  // Filter data by feedbackId if filter is present (contains search)
  const filteredData = useMemo(() => {
    if (!data) return []
    if (!feedbackIdSearch) return data
    // Contains search: check if the feedback ID contains the search string
    return data.filter(f => f.feedbackId?.toString().includes(feedbackIdSearch))
  }, [data, feedbackIdSearch])

  // Clear the ID filter
  const clearIdFilter = () => {
    searchParams.delete('id')
    setSearchParams(searchParams)
    setFilters(prev => ({ ...prev, feedbackId: undefined, statuses: ['New', 'In Progress'] }))
  }

  const stats = {
    total: filteredData.length,
    new: filteredData.filter((f) => f.status === 'New').length,
    inProgress: filteredData.filter((f) => f.status === 'In Progress').length,
    highPriority: filteredData.filter((f) => f.priority === 'High' || f.priority === 'Critical').length,
  }

  return (
    <div style={{ padding: '24px' }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <Title level={2}>Feedback Management</Title>
            <Text type="secondary">Triage and respond to feedback from all organizations</Text>
            {feedbackIdSearch && (
              <div style={{ marginTop: 8 }}>
                <Tag
                  color="blue"
                  closable
                  onClose={clearIdFilter}
                  style={{ fontSize: 14, padding: '4px 8px' }}
                >
                  Filtering: {filters.feedbackId}
                </Tag>
              </div>
            )}
          </div>
          <Space>
            <Button icon={<DownloadOutlined />}>
              Export
            </Button>
            <Button icon={<ReloadOutlined />} onClick={() => refetch()}>
              Refresh
            </Button>
          </Space>
        </div>

        <Row gutter={16}>
          <Col span={6}>
            <Card>
              <Statistic title="Total" value={stats.total} />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic title="New" value={stats.new} valueStyle={{ color: '#1890ff' }} />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic title="In Progress" value={stats.inProgress} valueStyle={{ color: '#fa8c16' }} />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic title="High Priority" value={stats.highPriority} valueStyle={{ color: '#ff4d4f' }} />
            </Card>
          </Col>
        </Row>

        <Card data-tour="feedback-filters">
          <FeedbackFilters
            isAdminView={true}
            filters={filters}
            onFilterChange={handleFilterChange}
          />
        </Card>

        <FeedbackTable
          data={filteredData}
          isAdminView={true}
          loading={isLoading}
          onRefresh={() => refetch()}
        />
      </Space>
      <TourComponent />
    </div>
  )
}
